import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Select } from 'antd';
import {
  faFileSignature,
  faHouse,
  faHouseUser,
  faMagnifyingGlass,
  faToolbox,
  faUserPlus,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import { useCreateClient } from '../../queries/clients';
import { useCreateProspect, useUpdateProspect } from '../../queries/prospects';
import { useCreateOwner } from '../../queries/owners';
import { useCreateVendor } from '../../queries/vendors';
import { useProperties } from '../../queries/properties';
import { formatMoney } from '../Properties/constants';
import { TRADES } from '../Vendors/constants';
import { Btn, I, Seg, cx } from '../Todos/ui';

const TYPES = [
  ['tenant', 'Tenant', faHouse],
  ['prospect', 'Prospect', faMagnifyingGlass],
  ['applicant', 'Applicant', faFileSignature],
  ['owner', 'Owner', faHouseUser],
  ['vendor', 'Vendor', faToolbox]
];

const EMPTY = {
  name: '',
  phone: '',
  email: '',
  listingId: null,
  leaseStart: '',
  leaseEnd: '',
  desired: null,
  budget: '',
  moveIn: '',
  props: [],
  company: '',
  trade: null,
  area: ''
};

const popup = (n) => n.parentNode;

export default function AddContactModal({ phone, onClose }) {
  const navigate = useNavigate();
  const [type, setType] = useState('tenant');
  const [v, setV] = useState(EMPTY);
  const up = (k) => (e) => setV((o) => ({ ...o, [k]: e?.target ? e.target.value : e }));
  const { rows: units } = useProperties();
  const createClient = useCreateClient();
  const createProspect = useCreateProspect();
  const updateProspect = useUpdateProspect();
  const createOwner = useCreateOwner();
  const createVendor = useCreateVendor();
  const busy = [createClient, createProspect, updateProspect, createOwner, createVendor].some((m) => m.isPending);

  const unitOptions = units.map((u) => ({ value: u._id, label: u.name }));
  const unit = units.find((u) => u._id === v.listingId);
  const label = TYPES.find((t) => t[0] === type)[1];
  const bad =
    !v.name.trim() ||
    !/\d{3}.*\d{4}/.test(v.phone) ||
    (type === 'owner' && !v.props.length) ||
    (type === 'vendor' && !v.company.trim());

  const base = { name: v.name.trim(), phone: v.phone.trim(), email: v.email.trim() };
  const done = (kind, id) => {
    onClose();
    navigate(`/contacts/${kind}/${id}`);
  };
  const save = async () => {
    if (type === 'tenant') {
      const lease = v.leaseStart && v.leaseEnd ? `${v.leaseStart} to ${v.leaseEnd}` : '';
      const res = await createClient.mutateAsync({ ...base, tenantStatus: 'Active', listingId: v.listingId, lease });
      return done('client', res.client._id);
    }
    if (type === 'prospect' || type === 'applicant') {
      const desired = units.find((u) => u._id === v.desired)?.name || '';
      const intake = Object.fromEntries(
        Object.entries({ budgetLow: v.budget.trim(), budgetHigh: v.budget.trim(), moveDate: v.moveIn }).filter(([, x]) => x)
      );
      const res = await createProspect.mutateAsync({ ...base, interestedProperty: desired, source: 'Portal', intake });
      if (type === 'applicant') await updateProspect.mutateAsync({ id: res.prospect._id, pipelineStage: 'Application Submitted' });
      return done('client', res.prospect._id);
    }
    if (type === 'owner') {
      const res = await createOwner.mutateAsync({ ...base, listingIds: v.props });
      return done('owner', res.owner._id);
    }
    const res = await createVendor.mutateAsync({
      company: v.company.trim(),
      contact: base.name,
      phone: base.phone,
      email: base.email,
      trade: v.trade || '',
      coverage: v.area.trim(),
      status: 'active'
    });
    return done('vendor', res.vendor._id);
  };
  const submit = () => save().catch(() => {});

  const field = (id, text, el) => (
    <div className="m-field">
      <label htmlFor={id}>{text}</label>
      {el}
    </div>
  );
  const inp = (id, k, o = {}) => <input id={id} className="m-inp" value={v[k]} onChange={up(k)} {...o} />;
  const unitSelect = (id, k) => (
    <Select
      id={id}
      showSearch
      optionFilterProp="label"
      placeholder="Pick a property or unit"
      value={v[k]}
      onChange={up(k)}
      options={unitOptions}
      getPopupContainer={popup}
    />
  );

  let typeFields;
  if (type === 'tenant')
    typeFields = (
      <>
        {field('ac-u', 'Property or unit', unitSelect('ac-u', 'listingId'))}
        <div className="m-grid3">
          {field('ac-ls', 'Lease start', inp('ac-ls', 'leaseStart', { type: 'date' }))}
          {field('ac-le', 'Lease end', inp('ac-le', 'leaseEnd', { type: 'date' }))}
          {field('ac-r', 'Monthly rent', <input id="ac-r" className="m-inp" readOnly value={unit?.rent ? formatMoney(unit.rent) : ''} placeholder="From the unit" />)}
        </div>
      </>
    );
  else if (type === 'prospect' || type === 'applicant')
    typeFields = (
      <>
        {field('ac-d', 'Desired unit', unitSelect('ac-d', 'desired'))}
        <div className="m-grid2">
          {field('ac-b', 'Budget (optional)', inp('ac-b', 'budget', { placeholder: 'Under $1,200' }))}
          {field('ac-m', 'Move-in date', inp('ac-m', 'moveIn', { type: 'date' }))}
        </div>
      </>
    );
  else if (type === 'owner')
    typeFields = (
      <>
        <div className="m-field">
          <label>Properties owned (pick one or more)</label>
          <div className="ac-props">
            {units.map((u) => {
              const on = v.props.includes(u._id);
              return (
                <label key={u._id} className={cx('m-check', 'ac-prop', on && 'on')}>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => up('props')(on ? v.props.filter((x) => x !== u._id) : [...v.props, u._id])}
                  />
                  {u.name}
                </label>
              );
            })}
          </div>
        </div>
        <div className="m-sub">{v.props.length} selected</div>
      </>
    );
  else
    typeFields = (
      <>
        <div className="m-grid2">
          {field('ac-co', 'Company', inp('ac-co', 'company', { placeholder: 'Lake Area Roofing' }))}
          {field(
            'ac-sv',
            'Services provided',
            <Select
              id="ac-sv"
              placeholder="Pick a trade"
              value={v.trade}
              onChange={up('trade')}
              options={TRADES.map((t) => ({ value: t, label: t }))}
              getPopupContainer={popup}
            />
          )}
        </div>
        {field('ac-ar', 'Service area', inp('ac-ar', 'area', { placeholder: 'Lake Charles, Moss Bluff, Sulphur' }))}
      </>
    );

  return createPortal(
    <div className={cx('aa', phone && 'ph')}>
      <div className="m-scrim top" onClick={onClose} />
      <div className="m-modal" role="dialog" aria-modal="true" aria-labelledby="ac-t" onKeyDown={(e) => e.key === 'Escape' && onClose()}>
        <div className="m-mh">
          <div className="m-row">
            <I icon={faUserPlus} />
            <h2 id="ac-t">Add contact</h2>
            <div className="grow" />
            <button type="button" className="m-x" onClick={onClose} aria-label="Close">
              <I icon={faXmark} />
            </button>
          </div>
        </div>
        <div className="m-mb">
          <div className="m-field">
            <label>Contact type</label>
            <Seg items={TYPES} value={type} onChange={setType} label="Contact type" />
          </div>
          <div className="m-grid2">
            {field(
              'ac-n',
              type === 'vendor' ? 'Contact name' : 'Full name',
              inp('ac-n', 'name', { placeholder: type === 'vendor' ? 'Who we call' : 'First and last name', autoFocus: true })
            )}
            {field('ac-p', 'Phone', inp('ac-p', 'phone', { inputMode: 'tel', placeholder: '(337) 555-0170' }))}
          </div>
          {field('ac-e', 'Email (optional)', inp('ac-e', 'email', { type: 'email', placeholder: 'name@mail.example' }))}
          {typeFields}
        </div>
        <div className="m-mf">
          <Btn onClick={onClose}>Cancel</Btn>
          <div className="grow" />
          <Btn kind="primary" icon={faUserPlus} disabled={bad || busy} onClick={submit}>
            Add {label.toLowerCase()}
          </Btn>
        </div>
      </div>
    </div>,
    document.body
  );
}
